import { memo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { IconButton } from 'components';
import ProgressBar from 'components/ComponentV2/ProgressBar';
import { USERINFO_LABELS } from 'hooks/useCandidateProfile';
import { useCandidateProfile } from 'lib/contexts/candidate-profile-context';
import AdditionalBadge from '../Sidebar/ProfileProgress/AdditionalBadge';

import RecommendedProfileSection from '../Sidebar/RecommendedProfileSection';
import OptionalProfileSection from '../Sidebar/OptionalProfileSection';

import styles from './MobileProfileProgress.module.scss';

interface MobileProfileProgressProps {
  onLabelClick: (label: USERINFO_LABELS) => void;
  hideProfileProgress: () => void;
}

export const MobileProfileProgress = memo(
  ({ onLabelClick, hideProfileProgress }: MobileProfileProgressProps) => {
    const [t] = useTranslation('candidate.profile');
    const { percentCompleted, completedTopics } = useCandidateProfile();
    const [isProfileSectionOpen, setIsProfileSectionOpen] = useState(false);
    const toggleProfileSection = () => {
      setIsProfileSectionOpen(!isProfileSectionOpen);
    };

    return (
      <section className={styles.container}>
        <section className={styles['inner-box']}>
          <section className={styles['main-content']}>
            <section className={styles['top-content']}>
              <p className={styles.text}>
                {percentCompleted
                  ? t('profile-progress.complete')
                  : t('profile-progress.title')}
              </p>
              {percentCompleted === 100 ? (
                <IconButton
                  iconName="icon_close"
                  size="xsmall"
                  className={styles.icon}
                  onClick={hideProfileProgress}
                />
              ) : (
                <AdditionalBadge />
              )}
            </section>
            <section className={styles['bottom-content']}>
              <section className={styles['progress-bar']}>
                <ProgressBar
                  percentCompleted={percentCompleted}
                  showProgressWhen0
                  color="Primary"
                  className={styles.progress}
                />
              </section>
              <section className={styles['percentage-content']}>
                <section className={styles['percent-n-outof']}>
                  <p className={styles.percent}>{percentCompleted}%</p>
                  <p className={styles.outof}>
                    {completedTopics} {t('profile-progress.outof')}
                  </p>
                </section>
                <IconButton
                  iconName={
                    isProfileSectionOpen ? 'arrow-up' : 'icon_arrow-down'
                  }
                  size="xsmall"
                  className={styles.icon}
                  onClick={toggleProfileSection}
                />
              </section>
            </section>
          </section>

          {isProfileSectionOpen && (
            <section className={styles['extended-content']}>
              <div className={styles['recommended-optional']}>
                <RecommendedProfileSection onLabelClick={onLabelClick} />
                <OptionalProfileSection onLabelClick={onLabelClick} />
              </div>
            </section>
          )}
        </section>
      </section>
    );
  }
);

MobileProfileProgress.displayName = 'MobileProfileProgress';
